import { Link } from "wouter";
import { ArrowLeft, Bot } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

export default function Datenschutz() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-card to-background">
      <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2">
            <Bot className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
            <span className="text-lg sm:text-xl font-bold text-primary font-playfair">Social Media A-Team</span>
          </div>
          <Link href="/">
            <Button variant="ghost" size="sm" data-testid="button-back-home">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Zurück
            </Button>
          </Link>
        </div>
      </header>

      <main className="container px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Card className="max-w-4xl mx-auto">
          <CardContent className="p-4 sm:p-6 lg:p-8 space-y-8">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-playfair text-primary" data-testid="text-datenschutz-title">
                Datenschutzerklärung
              </h1>
              <p className="text-sm text-muted-foreground mt-2">Stand: August 2026</p>
            </div>

            <section className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-semibold text-foreground">1. Verantwortlicher</h2>
              <div className="text-muted-foreground space-y-1">
                <p className="font-medium text-foreground">SumoBrands UG (haftungsbeschränkt)</p>
                <p>Mike Gierlich</p>
                <p>Plektrudisstr. 6, 50354 Hürth</p>
                <p>Telefon: +49 2233 6290626</p>
                <p>
                  E-Mail:{" "}
                  <a href="mailto:Info@sumobrands.ai" className="text-primary hover:underline">
                    Info@sumobrands.ai
                  </a>
                </p>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-semibold text-foreground">2. Überblick</h2>
              <div className="text-muted-foreground space-y-3">
                <p>
                  Wir verarbeiten personenbezogene Daten, um Ihnen die Plattform „The A-Team" bereitzustellen:
                  einen Dienst zur KI-gestützten Erstellung, Planung und Veröffentlichung von Social-Media-Inhalten.
                  Dabei verarbeiten wir insbesondere:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <span className="font-medium text-foreground">Kontodaten:</span> Name, E-Mail-Adresse,
                    Unternehmensangaben, Passwort (ausschließlich verschlüsselt als Hash gespeichert);
                  </li>
                  <li>
                    <span className="font-medium text-foreground">Inhaltsdaten:</span> Unternehmensprofile,
                    Marketingangaben, hochgeladene Fotos und Logos, erstellte und geplante Beiträge,
                    Chat-Nachrichten mit dem KI-Assistenten;
                  </li>
                  <li>
                    <span className="font-medium text-foreground">Zahlungsdaten:</span> über unseren
                    Zahlungsdienstleister (wir selbst speichern keine Kreditkartendaten);
                  </li>
                  <li>
                    <span className="font-medium text-foreground">Nutzungs- und Protokolldaten:</span> IP-Adresse,
                    Zeitpunkt, Browsertyp (Server-Logs).
                  </li>
                </ul>
                <p>
                  Rechtsgrundlagen sind Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung), Art. 6 Abs. 1 lit. f DSGVO
                  (berechtigte Interessen, z. B. Sicherheit der Systeme) und, soweit erforderlich, Art. 6 Abs. 1
                  lit. a DSGVO (Einwilligung).
                </p>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-semibold text-foreground">3. Registrierung und Kundenkonto</h2>
              <p className="text-muted-foreground">
                Für die Nutzung ist ein Kundenkonto erforderlich. Die im Registrierungs- und Onboardingprozess
                erhobenen Daten (Firmenname, Ansprechpartner, E-Mail, Branchen- und Marketingangaben) verarbeiten
                wir zur Vertragserfüllung (Art. 6 Abs. 1 lit. b DSGVO). Passwörter werden ausschließlich als
                kryptografischer Hash gespeichert. Die Daten werden für die Dauer des Vertragsverhältnisses
                gespeichert und nach Vertragsende gelöscht, soweit keine gesetzlichen Aufbewahrungspflichten
                bestehen.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-semibold text-foreground">
                4. KI-Funktionen (Inhaltserstellung und Chat-Assistent)
              </h2>

              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">4.1 Wie die KI-Erstellung funktioniert</h3>
                  <div className="text-muted-foreground space-y-3">
                    <p>
                      Zur Erstellung von Beitragstexten und Bildern sowie für den Chat-Assistenten „Lina" setzen
                      wir generative KI-Modelle von Drittanbietern ein. Die Verarbeitung wird über die
                      Automatisierungsplattform n8n (n8n GmbH, Berlin; Hosting in der EU) orchestriert. Dabei
                      werden die für die Content-Erstellung erforderlichen Daten (Unternehmensprofil,
                      Marketingangaben, hochgeladene Fotos, Chat-Nachrichten und -Verlauf) an folgende
                      Auftragsverarbeiter bzw. Drittanbieter übermittelt:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>OpenAI (USA) — Text- und Bilderzeugung</li>
                      <li>Anthropic (USA) — Texterzeugung</li>
                      <li>Google (USA/Irland) — Text- und Bilderzeugung (Gemini)</li>
                    </ul>
                    <p>
                      Die Übermittlung in die USA erfolgt auf Grundlage des EU-U.S. Data Privacy Framework bzw.
                      der EU-Standardvertragsklauseln. Nach den API-Bedingungen der genannten Anbieter werden
                      über die API übermittelte Inhalte nicht zum Training der Modelle verwendet.
                    </p>
                    <p>
                      Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO. <span className="font-medium text-foreground">Hinweis:</span>{" "}
                      Laden Sie nach Möglichkeit keine Fotos mit abgebildeten Personen hoch, ohne deren
                      Einwilligung eingeholt zu haben; für Inhalte, die Sie hochladen, sind Sie als Kunde
                      verantwortlich.
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    4.2 Transparenz nach der EU-KI-Verordnung
                  </h3>
                  <p className="text-muted-foreground">
                    Der Chat-Assistent „Lina" ist ein KI-System; Sie interagieren dort nicht mit einem Menschen
                    (Art. 50 Abs. 1 Verordnung (EU) 2024/1689). Von der Plattform erzeugte Inhalte sind
                    KI-generiert und werden, soweit nach Art. 50 Abs. 2 der Verordnung vorgeschrieben und nach
                    dem Stand der Technik möglich, in maschinenlesbarer Form als KI-generiert gekennzeichnet.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    4.3 Keine automatisierte Entscheidungsfindung
                  </h3>
                  <p className="text-muted-foreground">
                    Eine automatisierte Entscheidungsfindung mit rechtlicher Wirkung im Sinne des Art. 22 DSGVO
                    findet nicht statt. Erstellte Inhalte werden erst nach Ihrer Freigabe veröffentlicht.
                  </p>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-semibold text-foreground">5. Hosting und Speicherung</h2>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>
                  <span className="font-medium text-foreground">Applikations-Hosting:</span> Die Plattform wird
                  auf Servern von Hostinger International Ltd. in Frankfurt am Main (Deutschland) betrieben. Beim
                  Aufruf der Plattform werden Server-Logfiles (IP-Adresse, Zeitpunkt, Browsertyp, aufgerufene
                  Seite) auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO (Systemsicherheit) verarbeitet.
                </li>
                <li>
                  <span className="font-medium text-foreground">Datenbank:</span> Kundendaten werden in einer
                  PostgreSQL-Datenbank bei Neon Inc. gespeichert; das Hosting erfolgt in der Region eu-central-1
                  (Frankfurt am Main, Deutschland).
                </li>
                <li>
                  <span className="font-medium text-foreground">Datei-Speicherung:</span> Hochgeladene Fotos und
                  erzeugte Bilder werden bei Amazon Web Services (S3) in der Region eu-central-1 (Frankfurt am
                  Main, Deutschland) gespeichert.
                </li>
                <li>
                  <span className="font-medium text-foreground">Sitzungsverwaltung:</span> Session-Cookies sind
                  für den Betrieb technisch erforderlich (Art. 6 Abs. 1 lit. f DSGVO bzw. § 25 Abs. 2 TDDDG).
                </li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-semibold text-foreground">
                6. Veröffentlichung auf Social-Media-Plattformen
              </h2>
              <p className="text-muted-foreground">
                Zur Veröffentlichung und Planung Ihrer freigegebenen Beiträge setzen wir einen spezialisierten
                Dienstleister für Social-Media-Publishing als Auftragsverarbeiter ein. An diesen Dienstleister
                werden die freigegebenen Inhalte (Texte, Bilder) sowie die Verknüpfungsdaten Ihrer verbundenen
                Social-Media-Konten übermittelt. Soweit dabei Daten in Drittländern verarbeitet werden, erfolgt
                dies auf Grundlage der EU-Standardvertragsklauseln bzw. des EU-U.S. Data Privacy Framework. Mit
                der Veröffentlichung gelten zusätzlich die Datenschutzbestimmungen der jeweiligen Ziel-Plattform
                (z. B. LinkedIn Ireland, Meta Platforms Ireland).
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-semibold text-foreground">7. Zahlungsabwicklung (Stripe)</h2>
              <p className="text-muted-foreground">
                Die Zahlungsabwicklung erfolgt über Stripe Payments Europe, Ltd. (Irland); eine Übermittlung an
                Stripe Inc. (USA) ist möglich (EU-U.S. Data Privacy Framework). Stripe erhält die für die
                Abwicklung erforderlichen Daten (Name, E-Mail, Zahlungsmittel, Betrag). Wir selbst speichern
                keine vollständigen Zahlungsmitteldaten. Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-semibold text-foreground">8. E-Mail-Kommunikation</h2>
              <p className="text-muted-foreground">
                Transaktions-E-Mails (z. B. Registrierung, Rechnungen, Systemhinweise) versenden wir über
                Google Workspace (Gmail; Google Ireland Ltd.). Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-semibold text-foreground">9. Webanalyse (Google Analytics)</h2>
              <p className="text-muted-foreground">
                Diese Website nutzt Google Analytics 4, einen Webanalysedienst der Google Ireland Ltd. Google
                Analytics verwendet Technologien, die eine Analyse der Nutzung der Website ermöglichen
                (z. B. Geräte- und Nutzungsinformationen). Die Verarbeitung erfolgt auf Grundlage von Art. 6
                Abs. 1 lit. f DSGVO (Analyse und Verbesserung unseres Angebots); soweit eine Einwilligung
                abgefragt wird, erfolgt die Verarbeitung ausschließlich auf Grundlage von Art. 6 Abs. 1 lit. a
                DSGVO und § 25 Abs. 1 TDDDG. Eine Übermittlung an Google LLC (USA) erfolgt auf Grundlage des
                EU-U.S. Data Privacy Framework.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-semibold text-foreground">10. Speicherdauer</h2>
              <p className="text-muted-foreground">
                Wir speichern personenbezogene Daten nur so lange, wie es für die genannten Zwecke erforderlich
                ist. Nach Vertragsende werden Konto- und Inhaltsdaten gelöscht, soweit keine gesetzlichen
                Aufbewahrungspflichten (insbesondere §§ 147 AO, 257 HGB für Rechnungs- und Buchungsdaten)
                entgegenstehen.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-semibold text-foreground">11. Ihre Rechte</h2>
              <div className="text-muted-foreground space-y-3">
                <p>
                  Sie haben gegenüber uns folgende Rechte hinsichtlich Ihrer personenbezogenen Daten: Auskunft
                  (Art. 15 DSGVO), Berichtigung (Art. 16), Löschung (Art. 17), Einschränkung der Verarbeitung
                  (Art. 18), Datenübertragbarkeit (Art. 20) sowie Widerspruch gegen Verarbeitungen auf Grundlage
                  von Art. 6 Abs. 1 lit. f DSGVO (Art. 21). Erteilte Einwilligungen können Sie jederzeit mit
                  Wirkung für die Zukunft widerrufen.
                </p>
                <p>
                  Zuständige Aufsichtsbehörde ist die Landesbeauftragte für Datenschutz und Informationsfreiheit
                  Nordrhein-Westfalen (LDI NRW), Kavalleriestr. 2-4, 40213 Düsseldorf. Ihnen steht ein
                  Beschwerderecht bei jeder Datenschutz-Aufsichtsbehörde zu (Art. 77 DSGVO).
                </p>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-semibold text-foreground">
                12. Auftragsverarbeitung für unsere Kunden
              </h2>
              <p className="text-muted-foreground">
                Soweit Sie als Kunde Inhalte mit personenbezogenen Daten Dritter (z. B. Fotos von Mitarbeitern
                oder Gästen) hochladen, verarbeiten wir diese Daten als Auftragsverarbeiter in Ihrem Auftrag.
                Einen Auftragsverarbeitungsvertrag nach Art. 28 DSGVO stellen wir auf Anfrage bereit.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-semibold text-foreground">
                13. Änderungen dieser Datenschutzerklärung
              </h2>
              <p className="text-muted-foreground">
                Wir passen diese Datenschutzerklärung an, wenn sich die Verarbeitung oder die Rechtslage ändert.
                Es gilt die jeweils auf dieser Seite veröffentlichte Fassung.
              </p>
            </section>
          </CardContent>
        </Card>
      </main>

      <footer className="border-t border-border/40 py-6 sm:py-8 mt-8">
        <div className="container px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center space-x-2">
              <Bot className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
              <span className="font-semibold text-primary font-playfair text-sm sm:text-base">Social Media A-Team</span>
            </div>
            <div className="flex flex-wrap justify-center gap-4 text-xs sm:text-sm text-muted-foreground">
              <span>© 2026 SumoBrands</span>
              <Link href="/impressum" className="hover:text-primary hover:underline" data-testid="link-footer-impressum">
                Impressum
              </Link>
              <Link href="/datenschutz" className="hover:text-primary hover:underline" data-testid="link-footer-datenschutz">
                Datenschutz
              </Link>
              <Link href="/agb" className="hover:text-primary hover:underline" data-testid="link-footer-agb">
                AGB
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
